import { Component, inject, DestroyRef, Optional, Inject, SimpleChanges, Input  } from '@angular/core';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import {translate} from '@jsverse/transloco';
import { RouterSegments } from 'src/app/models/router-segments';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { Blueprint } from 'src/app/models/blueprints/blueprint';
import {ActivatedRoute, Router} from '@angular/router';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { SolutionVersion } from 'src/app/models/service/solution-version';
import { ComponentType } from 'src/app/models/service/component-type';
import { forkJoin, timer, switchMap, takeWhile } from 'rxjs';
import {MatDialog, MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import { SizingService } from 'src/app/services/api/sizing.service';
import { SizingProgressDialogComponent } from 'src/app/components/sizing-progress-dialog/sizing-progress-dialog.component';
import { SizingConfirmDialogComponent } from 'src/app/components/sizing-confirm-dialog/sizing-confirm-dialog.component';
import { SizingDialogMode } from 'src/app/models/sizing';
import { SizingEditDialogComponent } from '../sizing-edit-dialog/sizing-edit-dialog.component';
import { JsonValidationService } from 'src/app/services/util/json-validation.service';
import { sizingTemplateSchema } from 'src/app/schemas/sizingTemplate-schema';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { EnvironmentPages } from 'src/app/models/environment-pages/environment-pages';
import {SizingErrorUtil} from 'src/app/services/util/sizing-error.util';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { toTitleCase } from 'src/app/services/util/utils';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';
import { Attributes, Datazone, Permissions } from 'src/app/models/principal/permission';
import { TenantService } from 'src/app/services/api/tenant.service';
import { CloudProvider } from 'src/app/models/cloud-provider/cloud-provider';
import { PermissionsService } from 'src/app/services/util/permissions.service';

const SIZING_PERMISSIONS = [
  {
      key: PermissionKeys.SIZING_CREATE_NONPII,
      action: Permissions.SIZING_CREATE,
      attribute: Attributes.DATAZONE,
      attributeValue: Datazone.NONPII
   },
   {
      key: PermissionKeys.SIZING_CREATE_PII,
      action: Permissions.SIZING_CREATE,
      attribute: Attributes.DATAZONE,
      attributeValue: Datazone.PII
   },
   {
      key: PermissionKeys.SIZING_CREATE_LIVE,
      action: Permissions.SIZING_CREATE,
      attribute: Attributes.DATAZONE,
      attributeValue: Datazone.LIVE
   },
   { key: PermissionKeys.SIZING_UPDATE_NONPII, action: Permissions.SIZING_UPDATE, attribute: Attributes.DATAZONE, attributeValue: Datazone.NONPII },
   { key: PermissionKeys.SIZING_UPDATE_PII,    action: Permissions.SIZING_UPDATE, attribute: Attributes.DATAZONE, attributeValue: Datazone.PII },
   { key: PermissionKeys.SIZING_UPDATE_LIVE,   action: Permissions.SIZING_UPDATE, attribute: Attributes.DATAZONE, attributeValue: Datazone.LIVE },
]

enum SizingViewMode {
  FORM = 'FORM',
  JSON = 'JSON'
}

interface SizingMetric {
  key?: string;
  label: string;
  value: any;
  type: string;
  overrideAllowed: boolean;
  enum?: string[];
  unit?: string;
  isMemory?: any;
}

interface SizingSection {
  sectionTitle: string;
  metrics: SizingMetric[];
  isNested: boolean;
  sectionKey: string;
}

interface SizingCard {
  component: string;
  title: string;
  sections: SizingSection[];
  edited?: boolean;
}

@Component({
  selector: 'app-create-sizing',
  templateUrl: './create-sizing.component.html',
  styleUrl: './create-sizing.component.scss',
  standalone: false
})

export class CreateSizingComponent {
    @Input() data: any;
    private destroyRef = inject(DestroyRef);
    breadcrumbItems: BreadcrumbItem[];
    RouterSegments = RouterSegments;
    tenantId: string;
    instanceId: string;
    tenant: string;
    instance: any;
    isLoading = true;
    blueprint: Blueprint;
    parameters: any;
    solution: SolutionVersion;
    ComponentType = ComponentType;
    currentView: SizingViewMode = SizingViewMode.FORM;
    SizingViewMode = SizingViewMode;
    isGlobalLoading = false;
    isJsonLoading = false;
    sizingDocumentId: string | null = null;
    allSizingRecords: any[] = [];
    sizingTableData: any;
    isCompletedHandled = false;
    originalSizingCards: SizingCard[] = [];
    sizingCards: SizingCard[] = [];
    searchTerm: string = '';
    sizingDocumentName?: string;
    sizingCreatedDate?: string;
    sizingCreatedBy?: string;
    isSizingLoading = false;
    isSubmitting = false;
    EnvironmentPages = EnvironmentPages;
    isEditMode = false;
    sizingDocData: any;
    retrySizing = false;
    isCheckingPermission = true; 
    isUnauthorized = false;
    editorOptions = {
      theme: 'light',
      language: 'json',
      automaticLayout: true,
      readOnly: true,
    };

    jsonInput = {
      value: '',
      language: 'json'
    };
    validSchema: boolean;
    templateResponse = true;
    errorsWithComponent:any;
    sizingDocApplied: any;
    isSubmitClicked = false;
    hasNoSizingData = false;
    isAWS: boolean;
    dataZone: any;
    permissionsMap: { [key: string]: boolean; };
    isCreateSizingAllowed: boolean;
    filteredSizingCards: any;

  readonly INSTANCE_AVAILABLE_STATUS = [DeployStatus.DEPLOYED, DeployStatus.UPDATED,
      DeployStatus.UPDATE_FAILED, DeployStatus.TESTED, DeployStatus.TEST_FAILED,
      DeployStatus.CONFIGURED, DeployStatus.CONFIGURATION_FAILED,
      DeployStatus.FAILED, DeployStatus.FAILOVER_FAILED, DeployStatus.FAILBACK_FAILED, DeployStatus.PRE_FAILBACK_FAILED, DeployStatus.POST_FAILBACK_FAILED, DeployStatus.STOP_FAILED];

      constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private blueprintsService: BlueprintsService,
    private authService: AuthService,
    private dialog: MatDialog,
    private instancesService: InstancesService,
    private toastrService: CustomToastrService,
    private solutionsService: SolutionsService,
    private sizingService: SizingService, private permissionsService: PermissionsService,
    private jsonSchemaValidator: JsonValidationService, public tenantService: TenantService,
    @Optional() private dialogRef: MatDialogRef<CreateSizingComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) {}

  get isDialogMode(): boolean {
    return !!this.dialogRef;
  }

  ngOnInit(): void {
  const instanceId = this.dialogData?.instanceId || this.activatedRoute.snapshot.params.instanceId;
  const isEditRoute = !!this.activatedRoute.snapshot.params.sizingDocumentId;
  this.instancesService.getInstanceById(instanceId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((instance: any) => {
      const dataZone = instance?.dataZone;
      const permissionsMap = this.permissionsService.resolveAPIPermissions(SIZING_PERMISSIONS);
      const canCreate = (dataZone === Datazone.NONPII && permissionsMap[PermissionKeys.SIZING_CREATE_NONPII])
                     || (dataZone === Datazone.PII    && permissionsMap[PermissionKeys.SIZING_CREATE_PII])
                     || (dataZone === Datazone.LIVE   && permissionsMap[PermissionKeys.SIZING_CREATE_LIVE]);

      const canEdit = (dataZone === Datazone.NONPII && permissionsMap[PermissionKeys.SIZING_UPDATE_NONPII])
                   || (dataZone === Datazone.PII    && permissionsMap[PermissionKeys.SIZING_UPDATE_PII])
                   || (dataZone === Datazone.LIVE   && permissionsMap[PermissionKeys.SIZING_UPDATE_LIVE]);

      this.isUnauthorized = isEditRoute ? !canEdit : !canCreate;
      this.isCheckingPermission = false;
      if (this.isUnauthorized) {
        return;
      }
      this.tenant = this.authService.principal.provider;
      this.instanceId = instanceId;
      if (this.isDialogMode) {
        this.startSizingProcess();
        this.getParameters();
      } else {
        const stateData = history.state?.sizingTableData;
        this.sizingDocData = history.state?.sizingDocData;
        const appliedDoc = this.sizingDocData?.find((doc: any) => doc.isApplied);
        if (appliedDoc) {
          this.getAppliedSizingDoc(appliedDoc?.id);
        }
        if (stateData && Object.keys(stateData).length > 0 && !appliedDoc) {
          this.isEditMode = true;
          this.sizingTableData = stateData;
          this.loadExistingSizingDocument();
        } else {
          this.isEditMode = false;
          this.startSizingProcess();
        }
        this.getParameters();
      }
    });
}

  /* Fetches instance parameters */
 getParameters(): any {
    this.instanceId = this.dialogData?.instanceId || this.activatedRoute.snapshot.params.instanceId;
    this.instancesService.getInstanceParameters(this.instanceId).pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(data => {
      this.parameters = data?.parameters;
      this.loadData();
    }, (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      this.loadData();
    });
  }

  /* Transforms backend keys into user-friendly display labels */
  private formatLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  /* Normalizes a string for case-insensitive and format-insensitive comparison.*/
  private normalize(value: string): string {
  return value
    ?.toLowerCase()
    .replace(/[_:\-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  }

  /* Opens progress dialog and starts sizing template loading process */
  private startSizingProcess(): void {
    this.isGlobalLoading = true;
    const dialogRef = this.dialog.open(SizingProgressDialogComponent, {
      width: '566px',
      height: '225px',
      disableClose: true,
      data: {
        title: translate('SIZING.VALIDATE'),
        progress: 0,
        showCount:false
      }
    });
    this.loadSizingdata(dialogRef);
  }

  /* Fetch sizing template records from API and populate sizing cards */
  loadSizingdata(dialogRef?: any): void {
    this.isSizingLoading = true;
    // display progress until getting api response & validated against schema
    let progress = 0;
    const progressInterval = setInterval(() => {
      if (dialogRef?.componentInstance && progress < 90) {
        progress += 1;
        dialogRef.componentInstance.progress = progress;
      }}, 300);
    this.sizingService
      .getSizingTemplateByInstanceId(this.instanceId,undefined, 'xxs-small')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.templateResponse = response.length > 0 ? true : false;
          this.hasNoSizingData = !Array.isArray(response) || response.length === 0;
          if (this.hasNoSizingData) {
            this.isSizingLoading = false;
            this.isGlobalLoading = false;
            dialogRef?.close();
            return;
          }
          this.sizingCards = [];
          this.originalSizingCards = [];
          const records = Array.isArray(response) ? response : [];
          const templateSchema = this.jsonSchemaValidator.validate(records,sizingTemplateSchema);
          this.validSchema = templateSchema.valid;
          if (templateSchema.valid) {
            clearInterval(progressInterval);
            if (dialogRef?.componentInstance) { dialogRef.componentInstance.progress = 100;}
          this.allSizingRecords = records;
          this.sizingService.buildSchemaMap(records);
          const validRecords = records.filter(record =>
            record.template &&
            Object.keys(record.template).length > 0 &&
            record.template.properties &&
            Object.keys(record.template.properties).length > 0
          );
          const total = validRecords.length;
          if (dialogRef) {
            dialogRef.componentInstance.title = translate('SIZING.PREPARE');
            dialogRef.componentInstance.total = total;
            dialogRef.componentInstance.showCount = true;
          }
          let processed = 0;
          const processNext = () => {
            if (processed >= total) {
              if (dialogRef) {
                dialogRef.close();
              }
              this.isSizingLoading = false;
              this.isGlobalLoading = false;
              return;
            }
            const record = validRecords[processed];
            if (dialogRef?.componentInstance) {
              dialogRef.componentInstance.currentComponent =
                record.component;
              dialogRef.componentInstance.processed = processed + 1;
            }
            const card =this.buildSizingCards(record);
            const clonedCard = JSON.parse(JSON.stringify(card));
            this.originalSizingCards.push(clonedCard);
            this.sizingCards.push(JSON.parse(JSON.stringify(card)));
            this.filteredSizingCards = [...this.sizingCards];
            processed++;
            setTimeout(processNext, 400);
          };processNext();
        }else {
          if (dialogRef) {
            dialogRef.close();
          }
          this.isSizingLoading = false;
          this.isGlobalLoading = false;
          this.showErrorDialog(true, templateSchema, records);
        }
        },
        error: (error) => {
          clearInterval(progressInterval);
          this.retrySizing = true;
          dialogRef?.close();
          this.isSizingLoading = false;
          this.isGlobalLoading = false;
          this.handleSubmitSizingError(error, translate('SIZING.VALIDATION_FAILED'));
        }
      });
  }

  loadExistingSizingDocument(): void {
  this.isGlobalLoading = true;

  const dialogRef = this.dialog.open(SizingProgressDialogComponent, {
    width: '566px',
    height: '225px',
    disableClose: true,
    data: {
      title: translate('SIZING.VALIDATE'),
      progress: 0,
      showCount: false
    }
  });

  let progress = 0;

  const progressInterval = setInterval(() => {
    if (dialogRef?.componentInstance && progress < 90) {
      progress += 1;
      dialogRef.componentInstance.progress = progress;
    }
  }, 300);

  this.sizingService
    .getSizingTemplateByInstanceId(this.instanceId, undefined, 'xxs-small')
    .pipe(
      switchMap((templateRecords) => {

        this.allSizingRecords = templateRecords;
        this.sizingService.buildSchemaMap(templateRecords);

        return this.sizingService.getSizingDocsById(
          this.instanceId,
          this.sizingTableData
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe({

      next: (response) => {

        const records = response?.components || [];

        this.sizingDocumentName = response?.name;
        this.sizingCreatedDate = response?.createdAt;
        this.sizingCreatedBy = response?.createdBy;
        this.hasNoSizingData = records.length === 0;

        if (this.hasNoSizingData) {
          clearInterval(progressInterval);
          dialogRef.close();
          this.isGlobalLoading = false;
          return;
        }
        this.sizingCards = [];
        this.originalSizingCards = [];

        clearInterval(progressInterval);
        if (dialogRef?.componentInstance) {
          dialogRef.componentInstance.progress = 100;
        }

        this.validSchema = true;

        const total = records.length;

        dialogRef.componentInstance.title = translate('SIZING.PREPARE');
        dialogRef.componentInstance.total = total;
        dialogRef.componentInstance.showCount = true;

        let processed = 0;

        const processNext = () => {

          if (processed >= total) {
            dialogRef.close();
            this.isGlobalLoading = false;
            this.updateJsonFromCards();
            return;
          }

          const record = records[processed];

          if (dialogRef?.componentInstance) {
            dialogRef.componentInstance.currentComponent =
              record.component;
            dialogRef.componentInstance.processed = processed + 1;
          }

          const card = this.buildCardsFromDocument(record);
          const clonedCard = JSON.parse(JSON.stringify(card));
          this.originalSizingCards.push(clonedCard);
          this.sizingCards.push(JSON.parse(JSON.stringify(card)));
          this.filteredSizingCards = [...this.sizingCards];
          
          processed++;

          setTimeout(processNext, 400);
        };

        processNext();
      },

      error: () => {
        clearInterval(progressInterval);
        dialogRef.close();
        this.isGlobalLoading = false;
      }
    });
}

// to navigate back to environment details page on cancel click
	goToSizing(): void {
  const tenantId = this.activatedRoute.snapshot.params.id;
	this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT, this.tenantId, RouterSegments.BLUEPRINTS, this.activatedRoute.snapshot.params.blueprintId, RouterSegments.ENVIRONMENT, this.activatedRoute.snapshot.params.instanceId], { state: { data: 'SIZING DOCUMENTS' }});
  }

  // Changes made on edit popup
  hasChanges(): boolean {
    return this.sizingCards.some((card, i) => {
    const originalCard = this.originalSizingCards[i];

    return card.sections.some((section, j) => {
      const originalSection = originalCard.sections[j];

      return section.metrics.some((metric, k) => {
        const originalValue = originalSection.metrics[k].value;
        const currentValue = metric.value;

        return String(currentValue) !== String(originalValue);
      });
    });
  });
  } 

  /* Maps sizing template API response into structured sizing cards for UI rendering */
 private buildSizingCards(record: any): SizingCard  {
   const appliedDoc = this.sizingDocApplied?.components?.find((doc: any) => doc.component === record.component);
   const documentValues = appliedDoc?.document || {};


    const template = record?.template;
    if (!template?.properties) return null as any;
    const rootProperties = template.properties;
    const sections: SizingSection[] = [];
    const hasNestedSections = Object.values(rootProperties).some(
      (prop: any) => prop?.type === 'object' && prop?.properties
    );
    if (hasNestedSections) {
      Object.keys(rootProperties).forEach(sectionKey => {
        const sectionSchema = rootProperties[sectionKey];
        if (!sectionSchema?.properties) return;
        const metrics: SizingMetric[] = Object.keys(sectionSchema.properties).map(metricKey => {
          const prop = sectionSchema.properties[metricKey];

          const sectionDoc = documentValues[sectionKey] || {};
          const mapKey = `${record.component}.${sectionKey}.${metricKey}`;

          return {
            key: metricKey,
            label: prop?.title || this.formatLabel(metricKey),
            value: sectionDoc[metricKey] ?? prop?.default,
            type: this.sizingService.getType(
                record.component,
                sectionKey,
                metricKey
            ),
            overrideAllowed: prop?.['x-override-allowed'] ?? false,
            min: prop?.minimum ? Number(prop.minimum) : null,
            max: prop?.maximum ? Number(prop.maximum) : null
          };
        });
        sections.push({
          sectionTitle: sectionSchema.title || this.formatLabel(sectionKey),
          sectionKey: sectionKey,
          metrics,
          isNested: true
        });
      });
    } else {
      const metrics: SizingMetric[] = Object.keys(rootProperties).map(metricKey => {
        const prop = rootProperties[metricKey];
        return {
          key: metricKey,
          label: prop?.title || this.formatLabel(metricKey),
          value: prop?.default,
          type: prop?.type,
          overrideAllowed: prop?.['x-override-allowed'] ?? false,
          min: prop?.minimum ? Number(prop.minimum) : null,
          max: prop?.maximum ? Number(prop.maximum) : null
        };
      });
      sections.push({
        sectionTitle: template.title || record.component,
        sectionKey: record.component,
        metrics,
        isNested: false
      });
    }
    return {
      component: record.component,
      title: template.title,
      sections
    };
}


private buildCardsFromDocument(record: any): SizingCard {

  const document = record.document || {};
  const sections: SizingSection[] = [];

  const keys = Object.keys(document);

  if (!keys.length) {
    return {
      component: record.component,
      title: record.component,
      sections: []
    };
  }

  const firstValue = document[keys[0]];

  if (typeof firstValue === 'object' && !Array.isArray(firstValue)) {

    keys.forEach(sectionKey => {

      const sectionDoc = document[sectionKey] || {};

      const metrics = Object.keys(sectionDoc).map(propKey => {
        const rawValue = sectionDoc[propKey];
        const isMemory = typeof rawValue === 'string' && /^[0-9.]+g$/i.test(rawValue);
        let value = rawValue;
        if (isMemory) {
          value = rawValue
        }

        const type = this.sizingService.getType(
          record.component,
          sectionKey,
          propKey
        );

        return {
          key: propKey,
          label: this.formatLabel(propKey),
          value,
          type,
          isMemory,
          enum: null,
          unit: null,
          overrideAllowed: true
        };
        });

      sections.push({
        sectionTitle: sectionKey,
        sectionKey: sectionKey,
        metrics,
        isNested: true
      });

    });

  } else {

    const metrics = keys.map(propKey => {
      const rawValue = document[propKey];

      const isMemory = typeof rawValue === 'string' && /^[0-9.]+g$/i.test(rawValue);
      let value = rawValue;

      if (isMemory) {
        value = rawValue.replace(/g$/i, '');
      }

      const type = this.sizingService.getType(
        record.component,
        record.component,
        propKey
      );

      return {
        key: propKey,
        label: this.formatLabel(propKey),
        value,
        type,
        isMemory,
        enum: null,
        unit: null,
        overrideAllowed: true
      };
    });

    sections.push({
      sectionTitle: record.component,
      sectionKey: record.component,
      metrics,
      isNested: false
    });

  }

  return {
    component: record.component,
    title: record.component,
    sections
  };
}

editSizing(card: any) {
  const dialogRef = this.dialog.open(SizingEditDialogComponent, {
    width: '1086px',
    height: '480px',
    data: {
      capability: card.title,
      component: card.component,
      sections: JSON.parse(JSON.stringify(card.sections))
    }
  });
  dialogRef.afterClosed().subscribe(result => {
    if (result) {
      card.edited = true;
      card.sections = result.map(section => ({
        ...section,
        metrics: section.metrics.map(metric => {
          if (metric.isMemory && !metric.value.toString().includes('G')) {
            return {
              ...metric,
              value: metric.value
            };
          }
          return metric;
        })
    }));
      this.updateJsonFromCards();
    }
  });
}

/* Handles sizing submission */
submitSizing(): void {
  if (this.isSubmitClicked) return;
  this.isSubmitClicked = true;
  this.sizingService.getSizingDocs(this.instanceId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(freshDocs => {
      this.sizingTableData = freshDocs;
      const existingNames = (this.sizingDocData || []).map((d: any) =>
        d?.name?.toLowerCase().trim()
      );
      const generatedName = this.generateNextSizingName(existingNames, this.isDialogMode);
      const dialogData = this.isDialogMode
        ? { documentName: generatedName, disableName: true, environmentSize: null, mode: SizingDialogMode.SUBMIT }
        : { documentName: 'Default', nameSuffix: generatedName.replace(/^Sizing_[^_]+/i, ''), environmentSize: null, mode: SizingDialogMode.SUBMIT };
      const dialogRef = this.dialog.open(SizingConfirmDialogComponent, {
        width: '566px',
        height: this.isDialogMode ? '282px' : '320px',
        disableClose: true,
        data: dialogData
      });
      dialogRef.afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result: any) => {
          if (!result?.documentName) {
            this.isSubmitClicked = false;
            return;
          }
          const progressDialogRef = this.dialog.open(SizingProgressDialogComponent, {
            width: '566px',
            height: '225px',
            disableClose: true,
            data: {
              title: translate('SIZING.VALIDATE'),
              progress: 0,
              showCount: false
            }
          });
          let progress = 0;
          const interval = setInterval(() => {
            if (progressDialogRef?.componentInstance && progress < 90) {
              progress += 5;
              progressDialogRef.componentInstance.progress = progress;
            }
          }, 200);
          this.isSubmitting = true;
          this.sizingDocumentName = result?.documentName;
          const payload = this.buildSizingPayload();
          this.sizingService
            .postSizingDocs(payload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (response) => {
                clearInterval(interval);

                if (progressDialogRef?.componentInstance) {
                  progressDialogRef.componentInstance.progress = 100;
                }
                this.sizingDocumentId = response?.id;
                if (!this.sizingDocumentId) {
                  this.isSubmitting = false;
                  return;
                }
                setTimeout(() => {
                progressDialogRef.close();
                this.isSubmitClicked = false;
                this.startSizingPolling();
                }, 400);
              },
              error: (error) => {
                clearInterval(interval);
                this.isSubmitClicked = false;
                progressDialogRef.close();
                this.isSubmitting = false;
                this.handleSubmitSizingError(error);
              }
            });
            });
        });
}

updateSizing(): void {
  if (this.isSubmitClicked) return;
  this.isSubmitClicked = true;
  const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
    panelClass: 'default-dialog-wrapper',
    width: '566px',
    autoFocus: false,
    data: {
      title: translate('SIZING.EDIT_CONFIRM_TITLE'),
      message: translate('SIZING.EDIT_CONFIRM_MESSAGE'),
      confirmButtonText: translate('BUTTONS.CONFIRM')
    }
  });

  dialogRef.afterClosed()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((confirmed: boolean) => {

      if (!confirmed) {
        this.isSubmitClicked = false;
        return;
      }

      const progressDialogRef = this.dialog.open(SizingProgressDialogComponent, {
        width: '566px',
        height: '225px',
        disableClose: true,
        data: {
          title: translate('SIZING.VALIDATE'),
          progress: 0,
          showCount: false
        }
      });

      let progress = 0;
      const interval = setInterval(() => {
        if (progressDialogRef?.componentInstance && progress < 90) {
          progress += 5;
          progressDialogRef.componentInstance.progress = progress;
        }
      }, 200);

      const obj = {
        components: this.sizingCards.map(card => ({
          component: card.component,
          document: this.buildComponentDocument(card)
        }))
      };


      this.sizingService
        .putSizingDocsById(this.instanceId, this.sizingTableData, obj)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            clearInterval(interval);
            if (progressDialogRef?.componentInstance) {
              progressDialogRef.componentInstance.progress = 100;
            }
            setTimeout(() => {
              progressDialogRef.close();
              this.isSubmitClicked = false;
              const title = translate('SIZING.SIZING_TITLE');
              const message = translate('SIZING.DOCUMENT_UPDATED');
              this.toastrService.successMessage(message, title, this.instance?.name);
              this.goToSizing();
            }, 400);
          }, error: (err) => {
            clearInterval(interval);
            progressDialogRef.close();
            this.isSubmitClicked = false;
            this.handleSubmitSizingError(err);
          }
        });

    });

}

private handleSubmitSizingError(err: any, title?: string): void {
  const apiError = err?.error?.error ?? err?.error ?? err;
  let errorArray = [];
  if (apiError?.details?.length) {
    errorArray = apiError.details.map((d: any) => {
      return {
        code: d.code ?? apiError.code,
        message: d.message,
        details: d.field
      };
    });
  } else if (apiError?.message) {
    errorArray = [{
      code: apiError?.code,
      message: apiError?.message,
      details: ''
    }];
  }
  this.showErrorDialog(false, { title, descriptionError: errorArray})
}

/* Handles Polls sizing status */
private startSizingPolling(): void {
  const pollInterval = 3000;
  const total = this.sizingCards.length;
  let progress = 0;

  const progressDialogRef = this.dialog.open(SizingProgressDialogComponent, {
    width: '566px',
    height: '225px',
    disableClose: true,
    data: {
      title: translate('SIZING.GENERATE'),
      processed: 0,
      total: total,
      currentComponent: '',
      showCount: true
    }
  });

  timer(0, pollInterval)
    .pipe(
      switchMap(() =>
        this.sizingService.getSizingDocsById(this.instanceId, this.sizingDocumentId)
      ),
      takeWhile((response: any) => {
        const completedCount = response?.components?.length || 0;
        return completedCount < total && !this.isCompletedHandled;
      }, true),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe({
      next: (response) => {

        if (!response) return;

        const completedCount = response.components?.length || 0;

        const animateProgress = () => {

          if (progress < completedCount) {

            progressDialogRef.componentInstance.processed = progress + 1;

            progressDialogRef.componentInstance.currentComponent =
              this.sizingCards[progress]?.component || '';

            progress++;

            setTimeout(animateProgress, 400);

          } else if (completedCount === total && !this.isCompletedHandled) {

            this.isCompletedHandled = true;

            setTimeout(() => {
              progressDialogRef.close();
              this.isSubmitting = false;

              if (this.isDialogMode) {
                this.dialogRef.close(true);
              } else {
                this.goToSizing();
              }

              const title = translate('SIZING.SIZING_TITLE');
              const message = translate('SIZING.DOCUMENT_CREATED');
              this.toastrService.successMessage(message, title, this.instance?.name);

            }, 500);
          }
        };

        animateProgress();

        if (
          response.opstatus !== undefined &&
          response.httpStatusCode !== undefined &&
          (response.opstatus !== 0 || response.httpStatusCode !== 200)
        ) {
          progressDialogRef?.close();
          this.isSubmitting = false;
        }
      },
      error: () => {
        progressDialogRef?.close();
        this.isSubmitting = false;
      }
    });
}

/* Constructs POST payload from sizing cards */
private buildSizingPayload(): any {
  return {
    id: this.instanceId,
    name: this.sizingDocumentName,
    environmentSize: "xxs-small",
    components: this.sizingCards.map(card => ({
      component: card.component,
      document: this.buildComponentDocument(card)
    }))
  };
}

/* Maps a sizing card's sections and metrics into backend document format */
private buildComponentDocument(card: SizingCard): any {

  if (!card?.sections?.length) return {};

  const document: any = {};

  card.sections.forEach(section => {

    const sectionObject: any = {};

    section.metrics.forEach(metric => {
    if (!metric?.label) return;
      const key = metric.key || metric.label.toLowerCase().replace(/ /g, '_');

      let value = metric.value;

      if (metric.isMemory) {
        const memoryValue = String(value).replace(/g$/i, '');
        value = `${memoryValue}G`;
      }
      else {
        switch (metric.type) {

          case 'integer':
            value = value !== '' ? parseInt(value, 10) : null;
            break;

          case 'number':
            value = value !== '' ? parseFloat(value) : null;
            break;

          case 'string':
            value = value !== null && value !== undefined ? String(value) : '';
            break;

          default:
            value = value;
        }
      }

      sectionObject[key] = value;
    });

    if (section.isNested) {
      document[section.sectionKey || section.sectionTitle] = sectionObject;
    } else {
      Object.assign(document, sectionObject);
    }

  });

  return document;
}

  /* Generates the next Sizing_Default name. Dialog mode increments major version; routed mode increments minor. */
  private generateNextSizingName(existingNames: string[], majorIncrement = false): string {
    const versions = existingNames
      .filter(n => /^sizing_.+_v\d+\.\d+$/i.test(n))
      .map(n => {
        const match = n.match(/^sizing_.+_v(\d+)\.(\d+)$/i);
        return match ? { major: parseInt(match[1], 10), minor: parseInt(match[2], 10) } : null;
      })
      .filter(v => v !== null) as { major: number; minor: number }[];
    if (versions.length === 0) {
      return 'Sizing_Default_v1.0';
    }
    const highest = versions.reduce((a, b) =>
      a.major > b.major || (a.major === b.major && a.minor > b.minor) ? a : b
    );
    return majorIncrement
      ? `Sizing_Default_v${highest.major + 1}.0`
      : `Sizing_Default_v${highest.major}.${highest.minor + 1}`;
  }

  /* Handles Sizing step button behavior based on current sizing status */
  onSizingAction(): void {
    if (this.isEditMode) {
      this.updateSizing();
    } else {
      this.submitSizing();
    }
  }

  /* Applies search filter to sizing cards based on component, section, and metric values */
  applySearch(): void {
    if (!this.searchTerm?.trim()) {
      this.filteredSizingCards = [...this.sizingCards];
      return;
    }
    const term = this.normalize(this.searchTerm);
    this.filteredSizingCards = this.sizingCards
      .map(card => {
        if (this.normalize(card.component).includes(term) ||
          this.normalize(card.title || '').includes(term)) {
          return card;
        }
        const filteredSections = card.sections
          .map(section => {
            const sectionMatches =
              this.normalize(section.sectionTitle).includes(term);
            const filteredMetrics = section.metrics
              .filter(metric => {
                const combined = this.normalize(
                  `${metric.label} ${metric.value}`
                );
              return combined.includes(term);
            });
            if (sectionMatches) {
              return section;
            }
            if (filteredMetrics.length > 0) {
              return {
                ...section,
                metrics: filteredMetrics
              };
            }
            return null;
          })
          .filter(Boolean);
        if (filteredSections.length > 0) {
          return {
            ...card,
            sections: filteredSections
          };
        }
        return null;
      })
      .filter(Boolean) as SizingCard[];
}

/* Safely formats display values for UI rendering */
  formatDisplay(value: any): string {
    if (!this.instance) return '';
    if (value === null || value === undefined || value === '' || value === 'undefined') {
      return '-';
    }
    return value;
  }

  /* Switch between Form and Json tabs */
  switchView(mode: SizingViewMode): void {
    this.currentView = mode;
    if (mode === SizingViewMode.JSON) {
      this.initializeJsonView();
    }
}

/* Update Raw JSON data on editing the form */
private updateJsonFromCards(): void {

  const json = this.sizingCards.map(card => {

    const properties: any = {};

    card.sections.forEach(section => {

      const sectionProps: any = {};

      section.metrics.forEach(metric => {

        const key = metric.key || metric.label.toLowerCase().replace(/ /g, '_');

        let value = metric.value

        if (metric.isMemory) {
          const memoryValue = String(value).replace(/g$/i, '');
          value = `${memoryValue}G`;
        }

        switch (metric.type) {

          case 'integer':
            value = value !== '' ? parseInt(value, 10) : null;
            break;

          case 'number':
            value = value !== '' ? parseFloat(value) : null;
            break;

          case 'string':
            value = value !== null && value !== undefined ? String(value) : '';
            break;

          default:
            value = value;
        }

        sectionProps[key] = {
          value: value
        };

      });

      properties[section.sectionTitle] = {
        properties: sectionProps
      };

    });

    return {
      component: card.component,
      template: {
        properties
      }
    };

  });

  this.jsonInput.value = JSON.stringify(json, null, 2);
}

/* Initializes the JSON view configuration */
  private initializeJsonView(): void {
  this.isJsonLoading = true;
    setTimeout(() => {
      this.updateJsonFromCards();
      this.isJsonLoading = false;
    }, 200);
}

/* Triggers the download of the current JSON data */
  downloadJson(): void {
    if (!this.jsonInput?.value) return;
    const blob = new Blob([this.jsonInput.value],{ type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    const fileName = `${this.tenantId}-${this.blueprint?.name}-${this.instance?.name}-xxs-small`;
    downloadLink.download = `${fileName}.json`;
    downloadLink.click();
    window.URL.revokeObjectURL(url);
  }

  /* Loads instance, blueprint, and solution data for the Deploy workflow */
   loadData(): void {
    const blueprintId =
      this.dialogData?.blueprintId || this.activatedRoute.snapshot.paramMap.get('blueprintId');
    this.instancesService
      .getInstanceById(this.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(instance => {
        this.instance = instance;
        this.tenantId = instance.tenantId;
        forkJoin({
          blueprint: this.blueprintsService.getBlueprint(blueprintId!),
          solution: this.solutionsService.getSolutionVersion(
            this.instance.solutionId,
            this.instance.solutionVersionId
          )
        })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: ({ blueprint, solution }) => {
              this.blueprint = blueprint;
              this.solution = solution;
              this.configureBreadcrumb();
            },
            complete: () => {
              this.isLoading = false;
            }
          });
      });
}

/* Prompts confirmation and navigates back to Blueprint details page */
  goBack(): void {
    if (this.isDialogMode) {
      this.dialogRef.close();
      return;
    }
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: this.isEditMode ? translate('SIZING.EDIT_CANCEL_TITLE') : translate('SIZING.CANCEL_TITLE'),
        message: this.isEditMode ? translate('SIZING.EDIT_CANCEL_MESSAGE') : translate('SIZING.CANCEL_SIZING'),
        confirmButtonText: translate('BUTTONS.ABORT')
      }
    })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirm => {
        if (confirm) {
          this.goToSizing();
        }
      });
    }

  /* Builds and sets breadcrumb navigation for the Deploy workflow */
  private configureBreadcrumb(): void {
      this.breadcrumbItems = [];
      this.breadcrumbItems.push({icon: 'assets/images/home_icon.svg', url:[RouterSegments.TENANT, this.tenantId] });
      this.breadcrumbItems.push({label: translate('TENANT_LAYOUT.MENU.SERVICE_DEFINITION'), url:[RouterSegments.TENANT, this.tenantId, RouterSegments.SERVICE_DEFINITION]},
      {label: this.blueprint?.name, url:[RouterSegments.TENANT, this.tenantId, RouterSegments.SERVICE_DEFINITION, this.blueprint?.id]});
      this.breadcrumbItems.push({ label: this.instance?.name, url: [RouterSegments.TENANT, this.activatedRoute.snapshot.params.id, RouterSegments.SERVICE_DEFINITION,
      this.activatedRoute.snapshot.params.blueprintId, RouterSegments.ENVIRONMENT, this.activatedRoute.snapshot.params.instanceId] });
      this.breadcrumbItems.push({label: translate('SIZING.SIZING_TITLE'), action: () => this.goToSizing()});
      this.breadcrumbItems.push({label: this.isEditMode ? translate('UPDATE_MANAGER.EDIT_SIZING') : translate('INFRA.CREATE_SIZING')});
    }
  /**
     * Opens error dialog and handles retry/report actions after dialog closes
     * @param response
     */
    showErrorDialog(initial:boolean,response?:any,records?:any): void {
      if(initial) {
        const templateerror = SizingErrorUtil.groupTemplateErrors(response?.errors ?? []);
        const groupedErrors = SizingErrorUtil.groupErrorsByParent(templateerror);
        this.errorsWithComponent = groupedErrors.map((group: any) => ({...group,component: records[group.recordIndex]?.componentDisplayName  || records[group.recordIndex]?.component || ''}));
      }
      const errorDialogRef = this.dialog.open(ErrorDialogComponent, {
        width: '566px',
        minHeight: '342px',
        panelClass: 'flex-dialog',
        data: {
          title: response?.title ? toTitleCase(response.title) : (initial ? translate('SIZING.VALIDATION_FAILED') : translate('SIZING.SUBMITTING_FAILED')),
          errorDetails : this.errorsWithComponent,
          descriptionError: response?.descriptionError
        }
      });
      errorDialogRef.afterClosed().subscribe(result => {
        if (result === 'retry') {
          this.isSizingLoading = true;
          this.startSizingProcess();
        }
      });
    }

    openContactForm(): void {
    const url = `https://itsm-servicedesk.temenos.com`;
    window.open(url, '_blank');
  }

 getAppliedSizingDoc(sizingId: any): void {
  this.sizingService
    .getSizingDocsById(this.instanceId, sizingId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (response: any) => {  
           this.sizingDocApplied = response;
      }
    });
}

}
